/* eslint-disable @next/next/no-img-element */
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useMutation } from "@apollo/client";
import { useRecoilValue } from "recoil";
import clsx from "clsx";
import Tippy from "@tippyjs/react";
import { AnimatePresence } from "framer-motion";

import "tippy.js/dist/tippy.css";
import styles from "../../styles/tinymce.module.css";

import { engageState } from "../../atoms/platform";
import { ADD_SPEAKER } from "../../lib/graphql/engage.mutations";
import { apiGetAgenda } from "../../lib/api/engage";
import { apiPublicRegisterTopic } from "../../lib/api/speakers";
import {
  getGroupForTitleEngage,
  getItemNumber,
  getItemNumberOnly,
  getGroupForTitle,
  tarrantInlineText,
} from "../../helpers/text";
import { calculateDuration } from "../../helpers/timing";
import { textToHtml, textToHtmlTinyMCE } from "../../helpers/converter";
import {
  getSubheadingByGroupId,
  getNameByGroupId,
  getParentByGroupId,
} from "../../helpers/arrays";
import { isLessThan24HoursAway } from "../../helpers/timing";
import { PRINT_HOST, ENV_MODE } from "../../helpers/printing";

import { XIcon, PrinterIcon, DownloadIcon } from "@heroicons/react/outline";
import { MdOndemandVideo } from "react-icons/md";
import { FaRegComment } from "react-icons/fa";
import { IoChevronBack, IoArrowUp } from "react-icons/io5";
import { IoMdOpen } from "react-icons/io";
import { HiOutlineDocumentDuplicate } from "react-icons/hi";

import SpeakerModal from "./Dashboard/SpeakerModal";
import Success from "./Success";
import AgendaTopicFull from "./AgendaTopicFull";
import AgendaTopicInfoMobile from "./AgendaTopicInfoMobile";
import Loading from "../Shared/Loading";
import EngageHeader from "../Builders/Header/EngageHeader";
import Supplemental from "./Supplemental";
import SupplementalMobile from "./SupplementalMobile";
import TarrantCountyRegister from "./TarrantCountyRegister";
import TarrantCountyComment from "./TarrantCountyComment";

const findRegistrationTopicForGroup = (group, actionTopics) => {
  return actionTopics.find(
    (topic) =>
      topic.agendaGroup?.id === group.id && topic.registrationTopic === true,
  );
};

// TODO change from APOLLO to REST API for saving Registration
// This one needs to send out the notification

// TODO need experation for Tarrant County so if it's past the time allowed they can't register

const Agenda = () => {
  const router = useRouter();
  const { client, agenda } = router.query;
  const [showDetails, setShowDetails] = useState(false);
  const [mobileDetails, setMobileDetails] = useState(false);
  const [modal, setModal] = useState(false);
  const [topicDetails, setTopicDetails] = useState();
  const [success, setSuccess] = useState(false);
  const [tarrantSuccess, setTarrantSuccess] = useState(false);
  const [data, setData] = useState();
  const [isInIframe, setIsInIframe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState();
  const [error, setError] = useState(false);
  const [supplemental, setSupplemental] = useState({
    visible: false,
    meeting: null,
  });
  const [mobileSupplemental, setMobileSupplemental] = useState({
    visible: false,
    meeting: null,
  });
  const platform = useRecoilValue(engageState);
  const [addSpeaker] = useMutation(ADD_SPEAKER, {
    onCompleted: handleSuccess,
  });

  useEffect(() => {
    if (!router.isReady) return;
    if (!agenda) return;
    let active = true;
    async function fetchAgenda() {
      setLoading(true);
      try {
        const result = await apiGetAgenda(agenda);
        if (!active) return; // bail if unmounted / superseded
        if (result) {
          setData(result);
        } else {
          setError(true);
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    fetchAgenda();
    if (window.self !== window.top) setIsInIframe(true);

    return () => {
      active = false;
    }; // cleanup cancels the stale path
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, agenda]);

  function handleSuccess() {
    setModal(false);
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
    }, 2000);
  }

  async function handleSubmitSpeaker(type, data, topic) {
    if (type === "submit") {
      addSpeaker({
        variables: {
          input: { ...data, clientId: client },
          topic: topic,
        },
      });
    } else {
      setModal(false);
    }
  }

  async function handleTarrantSpeaker(type, formData, topic, selectedTopics) {
    if (type === "submit") {
      await apiPublicRegisterTopic(topic._id, {
        speaker: { ...formData, clientId: client },
        topic: topic.verbiage,
        client,
        scheduleDate: data.scheduleDate,
        selectedTopics,
      });
      setTarrantSuccess(true);
      setTimeout(() => {
        setModal(false);
        setTarrantSuccess(false);
        setModalType();
      }, 2000);
    } else {
      setModal(false);
      setModalType();
    }
  }

  function handleChosenTopic(topic) {
    setTopicDetails(topic);
    setShowDetails(true);
  }

  function handleMobileTopic(topic) {
    setTopicDetails(topic);
    setMobileDetails(true);
  }

  function handleRegister(topic) {
    setTopicDetails(topic);
    setModal(true);
  }

  return (
    <div className="pb-sm antialiased">
      {data && (
        <div
          className={clsx(
            "hidden sm:block max-w-full 2xl:max-w-7/8 mx-auto px-4 sm:px-6 md:px-8 pt-6 bg-white z-40",
          )}
        >
          {data?.coverOptions?.useHeader && (
            <EngageHeader
              header={data?.pdfAgenda.headerDetails}
              agendaFont={data?.department.agendaFont}
            />
          )}
          <div className="pb-2 border-b border-gray-400 flex flex-col items-end mt-2">
            {/* <div className="col-span-2 2xl:col-span-2 flex flex-col 2xl:items-center items-end justify-end"></div> */}
            <div className="mt-4 lg:mt-0 flex justify-end items-end space-x-2 col-span-4 2xl:col-span-1">
              <div className="flex items-center space-x-2 mr-2">
                {platform?.allowSupplemental &&
                  data?.attachments?.length > 0 && (
                    <Tippy
                      content="Supplemental Materials"
                      placement="bottom"
                      animation="scale"
                      delay={[500, 0]}
                    >
                      <div
                        className="flex px-2 py-0.5 md:px-4 md:py-1 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                        onClick={() =>
                          setSupplemental({
                            visible: true,
                            meeting: data,
                          })
                        }
                      >
                        <HiOutlineDocumentDuplicate className="h-4 w-4 md:h-6 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                      </div>
                    </Tippy>
                  )}

                {data?.videoUrl && (
                  <Tippy
                    content="Watch Video of the Meeting"
                    placement="bottom"
                    animation="scale"
                    delay={[500, 0]}
                  >
                    <div>
                      <a
                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${data?.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-2 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                        title="Watch Video"
                        role="button"
                        aria-label="Open Webpage with Video"
                      >
                        <MdOndemandVideo className="h-4 w-4 md:h-6 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                      </a>
                    </div>
                  </Tippy>
                )}
                {data?.publishStatus !== "draftreview" && (
                  <Tippy
                    content="Print Agenda"
                    placement="bottom"
                    animation="scale"
                    delay={[500, 0]}
                  >
                    <div>
                      <a
                        href={`${PRINT_HOST}/agenda/${data?.id}?mode=${ENV_MODE}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-2 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                        title="Print Cover Sheet"
                        role="button"
                        aria-label="Print Cover Sheet"
                      >
                        <PrinterIcon className="h-4 md:h-6 w-4 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                      </a>
                    </div>
                  </Tippy>
                )}

                {data?.agendaPdfUrl &&
                  data?.publishStatus !== "draftreview" && (
                    <Tippy
                      content="Download Agenda"
                      placement="bottom"
                      animation="scale"
                      delay={[500, 0]}
                    >
                      <div>
                        <a
                          href={data?.agendaPdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-2 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                          title="Download Agenda Packet"
                          role="button"
                          aria-label="Download Agenda Packet"
                        >
                          <DownloadIcon className="h-4 w-4 md:h-6 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                        </a>
                      </div>
                    </Tippy>
                  )}
                {data?.agendaPacketUrl &&
                  data?.publishStatus !== "draftreview" && (
                    <Tippy
                      content="Download Agenda Packet"
                      placement="bottom"
                      animation="scale"
                      delay={[500, 0]}
                    >
                      <div>
                        <a
                          href={data?.agendaPacketUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm md:text-base flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-1.5 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200focus:outline-none"
                          title="Download Agenda Packet"
                          role="button"
                          aria-label="Download Agenda Packet"
                        >
                          <DownloadIcon className="h-4 w-4 md:h-6 md:w-6 xl:h-8 xl:w-8 text-blueGray-200 cursor-pointer p-0 md:p-1 mr-1" />
                          Packet
                        </a>
                      </div>
                    </Tippy>
                  )}
              </div>
              {isInIframe && (
                <Tippy
                  content="Open Agenda in New Window"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <a
                    href={router.asPath}
                    target="_blank"
                    rel="noreferrer"
                    title="Open Agenda in New Window"
                    role="button"
                    aria-label="Open Agenda in New Window"
                  >
                    <IoMdOpen className="h-5 w-5 md:h-6 md:w-6 hover:text-blue-500 transition duration-200" />
                  </a>
                </Tippy>
              )}
              <XIcon
                className="h-6 w-6 xl:h-8 xl:w-8 2xl:h-10 2xl:w-10 text-gray-800 cursor-pointer hover:text-delete-btn transform duration-200 hover:rotate-180"
                aria-hidden="true"
                onClick={() => router.back()}
              />
            </div>
          </div>
          {data?.coverOptions?.useDateLocation && (
            <div
              className={clsx(
                "col-span-2 2xl:col-span-1 pt-1.5 pb-2",
                data?.department.agendaFont === "arial" && "font-arial",
                data?.department.agendaFont === "sans-serif" && "font-sans",
                data?.department.agendaFont === "timesNewRoman" &&
                  "font-timesNewRoman",
                data?.department.agendaFont === "noto" && "font-noto",
              )}
            >
              <p className="text-xs 2xl:text-lg text-black font-semibold">
                {data?.scheduleDate}
              </p>
              <p className="text-xs 2xl:text-lg text-black">
                {data?.room?.name}
              </p>
              <p className="text-xs 2xl:text-lg text-black">
                {data?.room?.address?.street}, {data?.room?.address?.city},
                {data?.room?.address?.state} {data?.room?.address?.zipCode}
              </p>
              {platform?.showDuration && (
                <p className="text-xs 2xl:text-base text-black italic">
                  Est. Duration: {calculateDuration(data?.actionTopics)}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Mobile header */}
      {data && (
        <div className="sm:hidden px-4 sm:px-6 pt-2 bg-white">
          <div className="pb-1 border-b border-gray-400">
            <div className="relative flex items-center justify-center">
              <h3 className="text-sm lg:text-lg xl:text-2xl leading-6 font-medium text-gray-900">
                {data?.department?.name}
              </h3>
              <IoChevronBack
                className="absolute top-1 left-0 h-6 w-6 text-gray-800 cursor-pointer hover:text-gray-400"
                aria-hidden="true"
                onClick={() => router.back()}
              />
            </div>
            {data?.coverOptions?.useDateLocation && (
              <div
                className={clsx(
                  "flex flex-col items-center",
                  data?.department.agendaFont === "arial" && "font-arial",
                  data?.department.agendaFont === "sans-serif" && "font-sans",
                  data?.department.agendaFont === "timesNewRoman" &&
                    "font-timesNewRoman",
                  data?.department.agendaFont === "noto" && "font-noto",
                )}
              >
                <p className="text-xs lg:text-lg text-gray-700 italic">
                  {data?.scheduleDate}
                </p>
                {platform?.showDuration && (
                  <p className="text-xs lg:text-lg text-gray-700 italic">
                    Est. Duration: {calculateDuration(data?.actionTopics)}
                  </p>
                )}

                <p className="text-xs lg:text-lg text-gray-700">
                  {data?.room.name}
                </p>
                <p className="text-xs lg:text-lg text-gray-700">
                  {data?.room.address?.street}, {data?.room.address?.city},
                  {data?.room.address?.state} {data?.room.address?.zipCode}
                </p>
              </div>
            )}

            <div className="mt-2 flex justify-end items-center space-x-2">
              {platform?.allowSupplemental && data?.attachments?.length > 0 && (
                <Tippy
                  content="Supplemental Resources"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <div
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      onClick={() => {
                        setMobileSupplemental({
                          visible: true,
                          meeting: data,
                        });
                      }}
                    >
                      <HiOutlineDocumentDuplicate className="h-5 w-5" />
                    </div>
                  </div>
                </Tippy>
              )}

              {data?.videoUrl && (
                <Tippy
                  content="Watch Video of the Meeting"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <a
                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${data?.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      title="Watch Video"
                      role="button"
                      aria-label="Open Webpage with Video"
                    >
                      <MdOndemandVideo className="h-5 w-5" />
                    </a>
                  </div>
                </Tippy>
              )}
              {data?.agendaPdfUrl && data?.publishStatus !== "draftreview" && (
                <Tippy
                  content="Download Agenda"
                  placement="bottom"
                  animation="scale"
                  delay={[500, 0]}
                >
                  <div>
                    <a
                      href={data?.agendaPdfUrl}
                      className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                      target="_blank"
                      rel="noreferrer"
                      title="Download Agenda Packet"
                      role="button"
                      aria-label="Download Agenda Packet"
                    >
                      <DownloadIcon className="h-5 w-5" />
                    </a>
                  </div>
                </Tippy>
              )}
              {data?.agendaPacketUrl &&
                data?.publishStatus !== "draftreview" && (
                  <Tippy
                    content="Download Agenda Packet"
                    placement="bottom"
                    animation="scale"
                    delay={[500, 0]}
                  >
                    <div>
                      <a
                        href={data?.agendaPacketUrl}
                        className="flex px-2 py-0.5 rounded-md items-center justify-evenly text-black focus:outline-none"
                        target="_blank"
                        rel="noreferrer"
                        title="Download Agenda Packet"
                        role="button"
                        aria-label="Download Agenda Packet"
                      >
                        <DownloadIcon className="h-5 w-5 mr-1" />
                        Packet
                      </a>
                    </div>
                  </Tippy>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Agenda Details */}
      {data && (
        <div className="hidden lg:block max-w-7xl 2xl:max-w-7/8 mx-auto px-4 sm:px-6 md:px-8">
          {/* Start Agenda */}
          <div className="mt-4 hidden lg:block">
            {/* Is Revised */}
            {data?.isRevised && (
              <div className="mt-0.5 w-full text-center">
                <p className="text-red-600 font-semibold text-sm xl:text-base 2xl:text-lg">
                  ** Revised **
                </p>
              </div>
            )}
            <div className={`relative mb-4 ${styles.tinyContainer}`}>
              {textToHtmlTinyMCE(data.pdfAgenda.agendaHeadingTitle)}
              {textToHtmlTinyMCE(data.pdfAgenda.agendaHeadingBlurb)}
            </div>
            {/* List */}
            <div>
              {data &&
                data?.groupOrder.map((group, index) => {
                  // const { name } = group;
                  const subheading = getSubheadingByGroupId(
                    group.id,
                    data?.groupSubheadings,
                  );
                  const parentGroup = getParentByGroupId(
                    group.id,
                    data?.groupSubheadings,
                  );
                  const name = getNameByGroupId(
                    group.id,
                    data?.groupSubheadings,
                    group.name,
                  );
                  const groupActions = data?.actionTopics
                    .map((topic) => {
                      if (
                        topic.agendaGroup.id === group.id &&
                        !topic.bulkTopic &&
                        !topic.registrationTopic
                      ) {
                        return topic;
                      }
                      return null;
                    })
                    .filter((topic) => topic !== null);
                  groupActions.sort((a, b) => a.order - b.order);
                  const registrationTopic = findRegistrationTopicForGroup(
                    group,
                    data?.actionTopics,
                  );
                  return (
                    <>
                      {group?.id.toString() === "682aa91b3c8f84005c5b9f81" && (
                        <div className={`mb-4 ${styles.tinyContainer}`}>
                          {textToHtmlTinyMCE(tarrantInlineText)}
                        </div>
                      )}
                      <div
                        key={`group-${index}`}
                        className={clsx(
                          `font-${data?.department.agendaFont}`,
                          group?.parentGroup && "ml-8",
                          "mb-4 xl:mb-8",
                        )}
                      >
                        {/* <div className="border-b border-gray-800"> */}
                        <div
                          className={clsx(
                            data?.coverOptions?.useSeperators &&
                              "border-b border-black",
                          )}
                        >
                          <p
                            className={clsx(
                              data?.department.agendaOptions.agendaGroupBold &&
                                "font-semibold",
                              data?.department.agendaOptions
                                ?.agendaGroupFontSize &&
                                `text-${data.department.agendaOptions.agendaGroupFontSize}`,
                            )}
                          >
                            {getGroupForTitleEngage(
                              data?.groupNumbering,
                              index,
                              data?.groupOrder,
                              data?.childNumbering,
                            )}
                            {name}
                          </p>
                        </div>
                        {registrationTopic &&
                          data?.publishStatus === "published" &&
                          !isLessThan24HoursAway(data?.scheduleIso) &&
                          client === "tarrantcountytx" && (
                            <button
                              className={clsx(
                                "inline-flex px-1.5 py-1 rounded-md bg-stone-500 text-white hover:bg-stone-700 transform duration-200 text-sm",
                              )}
                              onClick={(e) => {
                                setModalType("speak");
                                setModal(true);
                                setTopicDetails(registrationTopic);
                                e.stopPropagation();
                              }}
                            >
                              <IoArrowUp className="h-4 w-4 mr-1.5 mt-0.5" />
                              Register to Speak In-Person
                            </button>
                          )}
                        <p
                          className={clsx(
                            data?.department.agendaOptions
                              .groupSubheadingItalic && "italic",
                            data?.department.agendaOptions
                              ?.groupSubheadingSize &&
                              `text-${data.department.agendaOptions.groupSubheadingSize}`,
                            "text-black pt-1 ml-2 whitespace-pre-line text-justify",
                          )}
                        >
                          {subheading}
                        </p>
                        {groupActions?.map((topic, i) => (
                          <div key={topic.id}>
                            <div
                              className={clsx(
                                i === 0 ? "mt-4" : "",
                                "ml-4 xl:ml-8 mb-4 text-black select-none",
                              )}
                            >
                              <div className="flex">
                                {topic.allowRegister &&
                                  data?.publishStatus === "published" &&
                                  !topic.templateTopic &&
                                  platform?.embedSignUp &&
                                  client !== "tarrantcountytx" && (
                                    <div
                                      className={clsx(
                                        "mr-2 cursor-pointer text-blue-700 hover:text-blue-400 transform duration-200",
                                      )}
                                      onClick={(e) => {
                                        setModal(true);
                                        setTopicDetails(topic);
                                        e.stopPropagation();
                                      }}
                                    >
                                      <FaRegComment className="h-5 w-5" />
                                    </div>
                                  )}
                                <span
                                  className={clsx(
                                    "text-sm text-black flex-shrink-0",
                                    data?.department.agendaOptions.titleBold &&
                                      "font-semibold",
                                    data?.department.agendaOptions
                                      ?.titleFontSize &&
                                      `md:text-${data.department.agendaOptions.titleFontSize}`,
                                  )}
                                >
                                  {getGroupForTitle(
                                    data?.groupNumbering,
                                    index,
                                    data?.groupOrder,
                                    data?.childNumbering,
                                  )}
                                  {getItemNumberOnly(
                                    data.actionTopics,
                                    data.itemNumbering,
                                    topic,
                                    i,
                                    data?.groupNumbering,
                                    data?.childNumbering,
                                  )}
                                </span>
                                <p
                                  className={clsx(
                                    data?.publishStatus === "published" &&
                                      (topic?.hasDetails ||
                                        topic?.attachments) &&
                                      "cursor-pointer hover:text-blue-800 text-blue-700",
                                    data?.publishStatus === "manualminutes" &&
                                      (topic?.hasDetails ||
                                        topic?.attachments) &&
                                      "cursor-pointer hover:text-blue-800 text-blue-700",
                                    data?.publishStatus === "completed" &&
                                      (topic?.hasDetails ||
                                        topic?.attachments) &&
                                      "cursor-pointer hover:text-blue-800 text-blue-700",
                                    data?.publishStatus === "review" &&
                                      (topic?.hasDetails ||
                                        topic?.attachments) &&
                                      "cursor-pointer hover:text-blue-800 text-blue-700",
                                    data?.publishStatus === "minutes" &&
                                      (topic?.hasDetails ||
                                        topic?.attachments) &&
                                      "cursor-pointer hover:text-blue-800 text-blue-700",
                                    topic.previewPublish &&
                                      data?.publishStatus === "draft" &&
                                      (topic?.hasDetails ||
                                        topic?.attachments) &&
                                      "cursor-pointer hover:text-blue-800 text-blue-700",
                                    topic.previewPublish &&
                                      data?.publishStatus === "draftreview" &&
                                      (topic?.hasDetails ||
                                        topic?.attachments) &&
                                      "cursor-pointer hover:text-blue-800 text-blue-700",
                                    data?.department.agendaOptions.titleBold &&
                                      "font-semibold",
                                    data?.department.agendaOptions
                                      .titleUnderline &&
                                      "underline underline-offset-2",
                                    data?.department.agendaOptions
                                      ?.titleFontSize &&
                                      `md:text-${data.department.agendaOptions.titleFontSize}`,
                                    data?.department.agendaOptions?.titleIndent
                                      ? "ml-12"
                                      : "ml-4",
                                    data?.department.agendaOptions?.fullJustify
                                      ? "text-justify"
                                      : "text-left",
                                    "text-sm text-black whitespace-pre-line",
                                  )}
                                  // className="text-sm xl:text-base hd:text-lg font-medium truncate"
                                  onClick={() => {
                                    if (!topic.bulkTopic) {
                                      if (
                                        topic.hasDetails ||
                                        topic.attachments
                                      ) {
                                        switch (data?.publishStatus) {
                                          case "published":
                                          case "completed":
                                          case "review":
                                          case "manualminutes":
                                          case "minutes":
                                            handleChosenTopic(topic);
                                            break;
                                          case "draft":
                                          case "draftreview":
                                            if (topic.previewPublish) {
                                              handleChosenTopic(topic);
                                            }
                                            break;
                                          default:
                                            break;
                                        }
                                      }
                                    }
                                  }}
                                >
                                  {topic.title}
                                </p>
                              </div>
                              {/* Tarrant County */}
                              {topic.allowRegister &&
                                data?.publishStatus === "published" &&
                                !isLessThan24HoursAway(data?.scheduleIso) &&
                                !topic.templateTopic &&
                                client === "tarrantcountytx" && (
                                  <button
                                    className={clsx(
                                      "inline-flex px-1.5 py-1 rounded-md bg-stone-300 text-black hover:text-white hover:bg-stone-700 transform duration-200 text-sm",
                                    )}
                                    onClick={(e) => {
                                      setModalType("comment");
                                      setModal(true);
                                      setTopicDetails(topic);
                                      e.stopPropagation();
                                    }}
                                  >
                                    <FaRegComment className="h-4 w-4 mr-1.5 mt-0.5" />
                                    Written Comment
                                  </button>
                                )}
                              {topic.votedByBoard && (
                                <p
                                  className={clsx(
                                    data?.department.agendaOptions
                                      ?.detailsFontSize &&
                                      `text-${data.department.agendaOptions.detailsFontSize}`,
                                    data?.department.agendaOptions?.titleIndent
                                      ? "ml-16"
                                      : "ml-8",
                                    data?.department.agendaOptions?.fullJustify
                                      ? "text-justify"
                                      : "text-left",
                                    "whitespace-pre-line",
                                  )}
                                >
                                  &#40;{topic.votedByBoard?.name}&#41;
                                </p>
                              )}
                              {topic.useActionInfo &&
                                topic.useActionInfo === true && (
                                  <p
                                    className={clsx(
                                      data?.department.agendaOptions
                                        ?.titleIndent
                                        ? "ml-16"
                                        : "ml-8",
                                      data?.department.agendaOptions
                                        ?.fullJustify
                                        ? "text-justify"
                                        : "text-left",
                                      "whitespace-pre-line font-semibold text-red-700",
                                    )}
                                  >
                                    {topic.actionInfo}
                                  </p>
                                )}
                              {topic.topicBlurb.length > 0 && (
                                <span
                                  className={clsx(
                                    data?.department.agendaOptions
                                      ?.detailsFontSize &&
                                      `text-${data.department.agendaOptions.detailsFontSize}`,
                                    data?.department.agendaOptions?.titleIndent
                                      ? "ml-20"
                                      : "ml-8",
                                    data?.department.agendaOptions?.fullJustify
                                      ? "text-justify"
                                      : "text-left",
                                    "whitespace-pre-line inline-block",
                                  )}
                                >
                                  {topic.topicBlurb}
                                </span>
                              )}

                              {client !== "broomfield" &&
                                topic.subTopics?.length > 0 &&
                                topic.subTopics.map((sub, sIdx) => {
                                  if (client === "tarrantcountytx") {
                                    const letter = String.fromCharCode(
                                      97 + sIdx,
                                    ); // a, b, c, ...
                                    return (
                                      <p
                                        key={`sub-${sIdx}`}
                                        className={clsx(
                                          data?.department.agendaOptions
                                            ?.detailsFontSize &&
                                            `text-${data.department.agendaOptions.detailsFontSize}`,
                                          data?.department.agendaOptions
                                            .titleIndent
                                            ? "ml-20"
                                            : "ml-8",
                                          data?.department.agendaOptions
                                            .fullJustify
                                            ? "text-justify"
                                            : "text-left",
                                          "py-0.5",
                                        )}
                                      >
                                        {letter}&#41; {sub}
                                      </p>
                                    );
                                  } else {
                                    return (
                                      <p
                                        key={`sub-${sIdx}`}
                                        className={clsx(
                                          data?.department.agendaOptions
                                            ?.detailsFontSize &&
                                            `text-${data.department.agendaOptions.detailsFontSize}`,
                                          data?.department.agendaOptions
                                            .titleIndent
                                            ? "ml-20"
                                            : "ml-8",
                                          data?.department.agendaOptions
                                            .fullJustify
                                            ? "text-justify"
                                            : "text-left",
                                          "py-0.5",
                                        )}
                                      >
                                        {![
                                          "santafespringsca",
                                          "coloradocountytx",
                                        ].includes(client) && (
                                          <>&#8212; </>
                                        )}{" "}
                                        {sub}
                                      </p>
                                    );
                                  }
                                })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })}
            </div>
            <div className={`mt-4 ${styles.tinyContainer}`}>
              {textToHtmlTinyMCE(data.pdfAgenda.agendaFooterBlurb)}
            </div>
            {platform && (
              <div className="mt-6 flex w-full justify-between">
                <div>
                  <div className={`${styles.tinyContainer}`}>
                    {textToHtmlTinyMCE(data.pdfAgenda.agendaFooterNote)}
                  </div>
                  {platform.useSignImage ? (
                    <>
                      <img
                        src={platform.signatureImage}
                        alt="signature"
                        className="h-auto w-52"
                      />
                    </>
                  ) : (
                    <>
                      {data?.signature && (
                        <>
                          <img
                            src={data?.signature}
                            alt="signature"
                            className="h-auto w-52"
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            {platform?.useSeal && (
              <div className="flex justify-end">
                <img
                  src={platform.signatureSeal}
                  alt="signature"
                  className="h-32 w-32 rounded-full"
                />
              </div>
            )}
            <p className="text-xs 2xl:text-lg text-gray-700 italic">
              Date Posted: {data?.publishedDate}
            </p>
          </div>
          {/* End Content */}
        </div>
      )}

      {/* Mobile Data */}
      {data && (
        <div className="px-2 sm:px-4 lg:hidden">
          {/* Is Revised */}
          {data?.isRevised && (
            <div className="mt-0.5 w-full text-center">
              <p className="text-red-600 font-semibold text-mobile sm:text-sm">
                ** Revised **
              </p>
            </div>
          )}
          <div
            className={`relative mb-3 ${styles.tinyContainer}`}
            style={{ maxWidth: "8.5in", width: "100%", margin: "0 auto" }}
          >
            {textToHtmlTinyMCE(data.pdfAgenda.agendaHeadingTitle)}
            {textToHtmlTinyMCE(data.pdfAgenda.agendaHeadingBlurb)}
          </div>
          <div className="w-full flex flex-col space-y-2">
            {data?.groupOrder.map((group, index) => {
              const subheading = getSubheadingByGroupId(
                group.id,
                data?.groupSubheadings,
              );
              const parentGroup = getParentByGroupId(
                group.id,
                data?.groupSubheadings,
              );
              const name = getNameByGroupId(
                group.id,
                data?.groupSubheadings,
                group.name,
              );
              const groupActions = data?.actionTopics
                .map((topic) => {
                  if (
                    topic.agendaGroup.id === group.id &&
                    !topic.bulkTopic &&
                    !topic.registrationTopic
                  ) {
                    return topic;
                  }
                  return null;
                })
                .filter((topic) => topic !== null);
              groupActions.sort((a, b) => a.order - b.order);
              const registrationTopic = findRegistrationTopicForGroup(
                group,
                data?.actionTopics,
              );
              return (
                <>
                  {group?.id.toString() === "682aa91b3c8f84005c5b9f81" && (
                    <div className={`mb-4`}>
                      {textToHtml(tarrantInlineText)}
                    </div>
                  )}
                  <div
                    key={index}
                    className={clsx(
                      `font-${data?.department.agendaFont}`,
                      group?.parentGroup && "ml-2",
                    )}
                  >
                    <div className="border-b border-gray-800 mb-2">
                      <p className="text-black text-mobile sm:text-sm font-medium">
                        {getGroupForTitleEngage(
                          data?.groupNumbering,
                          index,
                          data?.groupOrder,
                          data?.childNumbering,
                        )}{" "}
                        {name}
                      </p>
                    </div>
                    {registrationTopic &&
                      data?.publishStatus === "published" &&
                      !isLessThan24HoursAway(data?.scheduleIso) &&
                      client === "tarrantcountytx" && (
                        <button
                          className={clsx(
                            "flex items-center md:justify-center pl-2 md:pl-0 py-1 rounded-md bg-stone-500 text-white hover:bg-stone-700 transform duration-200 text-sm mt-1.5 md:w-1/5",
                          )}
                          onClick={(e) => {
                            setModalType("speak");
                            setModal(true);
                            setTopicDetails(registrationTopic);
                            e.stopPropagation();
                          }}
                        >
                          <IoArrowUp className="h-4 w-4 mr-1.5 mt-0.5" />
                          Register to Speak In-Person
                        </button>
                      )}
                    <p className="text-mobile text-black pt-1 ml-2 mb-2 whitespace-pre-line text-justify font-medium">
                      {subheading}
                    </p>
                    {groupActions?.map((topic, i) => (
                      <div
                        key={topic.id}
                        className={clsx(
                          i === 0 ? "mt-4" : "",
                          "ml-4 mb-2 text-black select-none flex flex-col",
                        )}
                        onClick={() => {
                          if (!topic.bulkTopic) {
                            if (topic.hasDetails || topic?.attachments) {
                              switch (data?.publishStatus) {
                                case "published":
                                case "completed":
                                case "review":
                                case "manualminutes":
                                case "minutes":
                                  handleMobileTopic(topic);
                                  break;
                                case "draft":
                                case "draftreview":
                                  if (topic.previewPublish) {
                                    handleMobileTopic(topic);
                                  }
                                  break;
                                default:
                                  break;
                              }
                            }
                          }
                        }}
                      >
                        <p
                          className={clsx(
                            i === 0 ? "mt-4" : "",
                            data?.publishStatus === "published" &&
                              (topic?.hasDetails || topic?.attachments) &&
                              "cursor-pointer hover:text-blue-900 text-blue-700",
                            data?.publishStatus === "completed" &&
                              (topic?.hasDetails || topic?.attachments) &&
                              "cursor-pointer hover:text-blue-900 text-blue-700",
                            data?.publishStatus === "review" &&
                              (topic?.hasDetails || topic?.attachments) &&
                              "cursor-pointer hover:text-blue-900 text-blue-700",
                            data?.publishStatus === "minutes" &&
                              (topic?.hasDetails || topic?.attachments) &&
                              "cursor-pointer hover:text-blue-900 text-blue-700",
                            data?.publishStatus === "manualminutes" &&
                              (topic?.hasDetails || topic?.attachments) &&
                              "cursor-pointer hover:text-blue-900 text-blue-700",
                            topic.previewPublish &&
                              data?.publishStatus === "draft" &&
                              (topic?.hasDetails || topic?.attachments) &&
                              "cursor-pointer hover:text-blue-900 text-blue-700",
                            topic.previewPublish &&
                              data?.publishStatus === "draftreview" &&
                              (topic?.hasDetails || topic?.attachments) &&
                              "cursor-pointer hover:text-blue-900 text-blue-700",
                            "ml-2 text-mobile font-medium inline-flex text-black whitespace-pre-line",
                          )}
                          // className="ml-2 text-mobile font-medium inline-flex"
                        >
                          {data?.publishStatus === "published" &&
                            !topic.templateTopic &&
                            topic.allowRegister &&
                            platform?.embedSignUp &&
                            client !== "tarrantcountytx" && (
                              <FaRegComment
                                onClick={(e) => {
                                  setModal(true);
                                  setTopicDetails(topic);
                                  e.stopPropagation();
                                }}
                                className="mt-0.5 h-3 w-3 mr-2 cursor-pointer text-blue-700 hover:text-blue-400 transform duration-200 hidden md:block shrink-0"
                              />
                            )}
                          {getGroupForTitle(
                            data?.groupNumbering,
                            index,
                            data?.groupOrder,
                            data?.childNumbering,
                          )}
                          {getItemNumber(
                            data.actionTopics,
                            data.itemNumbering,
                            topic,
                            i,
                            data?.groupNumbering,
                            data?.childNumbering,
                          )}{" "}
                        </p>
                        {topic.allowRegister &&
                          data?.publishStatus === "published" &&
                          !topic.templateTopic &&
                          !isLessThan24HoursAway(data?.scheduleIso) &&
                          client === "tarrantcountytx" && (
                            <button
                              className={clsx(
                                "flex items-center md:justify-center pl-2 md:pl-0 py-1 rounded-md bg-stone-500 text-white hover:bg-stone-700 transform duration-200 text-sm mt-1.5 md:w-1/5",
                              )}
                              onClick={(e) => {
                                setModalType("comment");
                                setModal(true);
                                setTopicDetails(topic);
                                e.stopPropagation();
                              }}
                            >
                              <FaRegComment className="h-4 w-4 mr-1.5 mt-0.5" />
                              Written Comment
                            </button>
                          )}
                        {topic.useActionInfo &&
                          topic.useActionInfo === true && (
                            <p className="ml-2 text-mobile whitespace-pre-line font-semibold text-red-700">
                              {topic.actionInfo}
                            </p>
                          )}
                        {topic.topicBlurb.length > 0 && (
                          <p className="ml-2 text-mobile whitespace-pre-line font-medium">
                            {topic.topicBlurb}
                          </p>
                        )}

                        {client !== "broomfield" &&
                          topic.subTopics?.length > 0 &&
                          topic.subTopics.map((sub, sIdx) => {
                            if (client === "tarrantcountytx") {
                              const letter = String.fromCharCode(97 + sIdx); // a, b, c, ...
                              return (
                                <p
                                  key={`sub-${sIdx}`}
                                  className="ml-2 py-0.5 text-mobile font-medium"
                                >
                                  {letter}&#41; {sub}
                                </p>
                              );
                            } else {
                              return (
                                <p
                                  key={`sub-${sIdx}`}
                                  className="ml-2 py-0.5 text-mobile font-medium"
                                >
                                  {![
                                    "santafespringsca",
                                    "coloradocountytx",
                                  ].includes(client) && <>&#8212; </>}{" "}
                                  {sub}
                                </p>
                              );
                            }
                          })}
                      </div>
                    ))}
                  </div>
                </>
              );
            })}
            <div className="mt-4">
              {textToHtml(data?.pdfAgenda.agendaFooterBlurb)}
            </div>
            {platform && (
              <div className="mt-6 flex w-full justify-between">
                {platform.useSignImage ? (
                  <>
                    <img
                      src={platform.signatureImage}
                      alt="signature"
                      className="h-auto w-40"
                    />
                  </>
                ) : (
                  <>
                    {data?.signature && (
                      <>
                        <img
                          src={data?.signature}
                          alt="signature"
                          className="h-auto w-40"
                        />
                      </>
                    )}
                  </>
                )}
                {textToHtml(data?.pdfAgenda.agendaFooterNote)}
              </div>
            )}
            {platform?.useSeal && (
              <div>
                <img
                  src={platform.signatureSeal}
                  alt="signature"
                  className="h-16 w-16 rounded-full"
                />
              </div>
            )}
          </div>
          <p className="text-xs 2xl:text-lg text-gray-700 italic mt-2">
            Date Posted: {data?.publishedDate}
          </p>
        </div>
      )}

      {modal && client !== "tarrantcountytx" && (
        <SpeakerModal
          show={modal}
          visible={handleSubmitSpeaker}
          topic={topicDetails}
        />
      )}
      <AnimatePresence>
        {modal && modalType === "comment" && client === "tarrantcountytx" && (
          <TarrantCountyComment
            saveSpeaker={handleTarrantSpeaker}
            topic={topicDetails}
            tarrantSuccess={tarrantSuccess}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {modal && modalType === "speak" && client === "tarrantcountytx" && (
          <TarrantCountyRegister
            saveSpeaker={handleTarrantSpeaker}
            topic={topicDetails}
            tarrantSuccess={tarrantSuccess}
            meeting={agenda}
          />
        )}
      </AnimatePresence>
      {showDetails && (
        <AgendaTopicFull
          visible={setShowDetails}
          topic={topicDetails.id}
          enablePri={platform?.enablePriorities}
          client={client}
        />
      )}
      {supplemental?.visible && (
        <Supplemental
          meeting={supplemental?.meeting}
          update={setSupplemental}
        />
      )}
      <AnimatePresence>
        {mobileDetails && (
          <AgendaTopicInfoMobile
            visible={setMobileDetails}
            topic={topicDetails.id}
            enablePri={platform?.enablePriorities}
            client={client}
            register={handleRegister}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {mobileSupplemental?.visible && (
          <SupplementalMobile
            meeting={mobileSupplemental?.meeting}
            update={setMobileSupplemental}
          />
        )}
      </AnimatePresence>

      {success && <Success />}
      {loading && <Loading />}
      {error && (
        <div className="fixed z-10 inset-0 overflow-hidden">
          <div className="relative flex flex-col min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <img
              src="/images/jmCircle.svg"
              className="h-12 2xl:h-32 w-auto absolute top-4 left-4 z-50"
              alt="justmeetings logo"
            />
            <div className="fixed inset-0 bg-coolGray-900 transition-opacity"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
              &#8203;
            </span>
            {/* Start Modal */}
            <div className="inline-block bg-coolGray-900 rounded-lg text-left transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:px-6 sm:pt-2 sm:pb-8">
              <div className="grid grid-cols-1 gap-2 my-10">
                <div>
                  <div className="mt-3 text-center">
                    <h3
                      className="text-lg 2xl:text-4xl leading-6 font-bold text-blueGray-200"
                      id="modal-title"
                    >
                      Invalid Meeting
                    </h3>
                    <div className="mt-2 xl:mt-8">
                      <p className="text-lg text-blueGray-300 mb-2 text-center font-medium">
                        Your url is using{" "}
                        <span className="font-bold text-white">{agenda}</span>,
                        which is not a valid meeting within JustMeetings.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* End Modal */}
          </div>
        </div>
      )}
      {data?.publishStatus === "draft" && (
        <div className="absolute top-0 left-0 bg-slate-700 h-screen w-full z-50 flex items-center justify-center">
          <p className="text-lg 2xl:text-2xl font-bold text-white">
            This agenda is currently unavailable
          </p>
        </div>
      )}
    </div>
  );
};

export default Agenda;
